import { VariantProps } from 'class-variance-authority';

import { cn, formatToDecimalPlaces, splitPrice } from '@/lib/utils';
import {
  CurrencyVariant,
  DecimalPriceVariant,
  IntegralPriceVariant,
  OriginalPriceVariant,
  PriceBlockVariant,
  PriceFrequencyVariant,
} from '@/styles/variants/price-block';

import { Lozenge } from './lozenge';

type T_PriceBlock = {
  price: number | string;
  frequency: string;
  currency?: string;
  originalPrice?: number | string;
  note?: string;
  lozengeText?: string;
  currencyClassName?: string;
  decimalBlockClassName?: string;
  priceFrequencyClassName?: string;
};

type T_DivTag = React.HTMLAttributes<HTMLDivElement>;
type T_SpanTag = React.HTMLAttributes<HTMLSpanElement>;

export const Currency: React.FC<
  T_SpanTag & { currency: string } & VariantProps<typeof CurrencyVariant>
> = ({ currency, className, size, ...props }) => (
  <span className={cn(CurrencyVariant({ size }), className)} {...props}>
    {currency}
  </span>
);
export const OriginalPrice: React.FC<
  T_SpanTag & { price: string | number; frequency: string; currency?: string } & VariantProps<
      typeof OriginalPriceVariant
    >
> = ({ price, currency, frequency, className, size, ...props }) => (
  <span className={cn(OriginalPriceVariant({ size }), className)} {...props}>
    {currency}
    {price}
    {frequency}
  </span>
);
export const PriceNote: React.FC<
  T_SpanTag & { note: string } & VariantProps<typeof PriceBlockVariant>
> = ({ className, note, size, ...props }) => (
  <span className={cn(PriceBlockVariant({ size }), className)} {...props}>
    {note}
  </span>
);
export const IntegralBlock: React.FC<
  T_DivTag & { price: number | string } & VariantProps<typeof IntegralPriceVariant>
> = ({ price, className, size, ...props }) => (
  <div className={cn(IntegralPriceVariant({ size }), className)} {...props}>
    {price}
  </div>
);
export const DecimalBlock: React.FC<
  T_SpanTag & { price: number | string } & VariantProps<typeof DecimalPriceVariant>
> = ({ price, className, size, ...props }) => (
  <span className={cn(DecimalPriceVariant({ size }), className)} {...props}>
    .{price}
  </span>
);
export const PriceFrequency: React.FC<
  T_SpanTag & { frequency: string } & VariantProps<typeof PriceFrequencyVariant>
> = ({ frequency, className, size, ...props }) => (
  <span className={cn(PriceFrequencyVariant({ size }), className)} {...props}>
    {frequency}
  </span>
);
export const PriceBlock: React.FC<
  T_DivTag & T_PriceBlock & VariantProps<typeof PriceBlockVariant>
> = ({
  price,
  frequency,
  currency = '$', // Default Currency
  originalPrice,
  note,
  lozengeText,
  className,
  size,
  currencyClassName,
  decimalBlockClassName,
  priceFrequencyClassName,
  ...props
}) => {
  const decimalPrice = formatToDecimalPlaces(price);
  const [integralPart, decimalPart] = splitPrice(decimalPrice);
  return (
    <div
      className={cn(
        'flex flex-col gap-1 text-color-content-text-primary',
        PriceBlockVariant({ size }),
        className
      )}
      {...props}
    >
      {lozengeText && <Lozenge />}
      {originalPrice && (
        <OriginalPrice
          price={originalPrice}
          size={size}
          currency={currency}
          frequency={frequency}
        />
      )}
      <div className="flex gap-1">
        <Currency currency={currency} size={size} className={`pt-2 ${currencyClassName}`} />
        <IntegralBlock className={className} size={size} price={integralPart} />
        <div className="flex flex-col justify-center">
          <DecimalBlock size={size} price={decimalPart} className={`${decimalBlockClassName}`} />
          <PriceFrequency
            frequency={frequency}
            size={size}
            className={`text-color-content-text-tertiary ${priceFrequencyClassName}`}
          />
        </div>
      </div>
      {note && <PriceNote className={className} size={size} note={note} />}
    </div>
  );
};
